import { v4 as uuidv4 } from "uuid";
import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import WordSpinnerQuizPreview from "../../features/previews/word_spinner_quiz/WordSpinnerQuizPreview";
import { GetMigratedProperties } from "../../features/project_migration/InteractionMigrationHelper";
import { ValidationHelper } from "../../features/validation/ValidationHelper";
import { Actor } from "../Actor";
import { requiredGlobalQuizProperties } from "../GlobalQuizProperty";
import { InteractionProperty } from "../InteractionProperty";
import { IAnswer, IFormQuiz } from "../Quiz";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";
import { Prop } from "../Prop";
import { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";

export interface IWordSpinnerQuiz extends IFormQuiz {
  formattedSentence: string;
}

export const requiredWordSpinnerProperties: InteractionProperty[] = [
  ...requiredGlobalQuizProperties,
];

export function CreateWordspinnerQuiz(keys: string[]): IWordSpinnerQuiz {
  return {
    id: uuidv4(),
    name: "Word Spinner Quiz",
    type: InteractionType.WordSpinnerQuiz,
    title: keys[0],
    formattedSentence: keys[1],
    answers: [],
    properties: requiredWordSpinnerProperties,
  };
}

export function CreateWordspinnerQuizCopy(
  titleKey: string,
  sentencePart1Key: string,
  sentencePart2Key: string,
  formattedSentenceKey: string,
  rawSentenceKey: string,
  correctIndex: number,
  answers: IAnswer[],
  properties: InteractionProperty[] | undefined,
): IWordSpinnerQuiz {
  return {
    id: uuidv4(),
    name: "Word Spinner Quiz",
    type: InteractionType.WordSpinnerQuiz,
    title: titleKey,
    formattedSentence: formattedSentenceKey,
    answers: answers,
    properties: properties,
  };
}

interface LegacyWordspinner {
  correctAnswer: IAnswer;
}

interface LegacyWordspinner {
  correctAnswer: IAnswer;
}

export const wordSpinnerQuizInteraction = (): InteractionFactoryEntity => {
  const component = <WordSpinnerQuizPreview />;

  const create = (getKeys: (amount: number) => string[]): IWordSpinnerQuiz => {
    const keys = getKeys(2);
    return {
      id: uuidv4(),
      name: "Word Spinner Quiz",
      type: InteractionType.WordSpinnerQuiz,
      title: keys[0],
      formattedSentence: keys[1],
      answers: [],
      properties: requiredWordSpinnerProperties,
    };
  };
  const migrate = (
    from: number,
    to: number,
    interaction: Interaction,
  ): IWordSpinnerQuiz => {
    var converted = interaction as IWordSpinnerQuiz;
    var updatedInteraction: IWordSpinnerQuiz = { ...converted };

    if (from < 4) {
      const oldInteraction: any = JSON.stringify(interaction);
      const legacy: LegacyWordspinner = JSON.parse(oldInteraction);
      const newAnswer = legacy.correctAnswer;

      if (newAnswer !== undefined) {
        updatedInteraction = {
          ...converted,
          answers: [newAnswer, ...converted.answers],
        };
      }
    }

    if (updatedInteraction.properties === undefined)
      return {
        ...updatedInteraction,
        properties: requiredWordSpinnerProperties,
      };

    var newProperties: InteractionProperty[] = GetMigratedProperties(
      requiredWordSpinnerProperties,
      updatedInteraction.properties,
    );
    console.log(
      "[WordSpinnerQuiz] migrated " +
        (newProperties.length - updatedInteraction.properties.length) +
        " properties",
    );

    return { ...updatedInteraction, properties: newProperties };
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[],
    replaceKey: (from: string, to: string) => void,
    getAnswerArray: (
      originalAnswers: IAnswer[],
      newKeys: string[],
    ) => IAnswer[],
  ): IWordSpinnerQuiz => {
    const quiz = interaction as IWordSpinnerQuiz;
    const newKeys = getKeys(quiz.answers.length + 2);
    replaceKey(quiz.title, newKeys[newKeys.length - 1]);
    replaceKey(quiz.formattedSentence, newKeys[newKeys.length - 2]);

    return {
      id: uuidv4(),
      name: interaction.name,
      type: InteractionType.WordSpinnerQuiz,
      title: newKeys[newKeys.length - 1],
      formattedSentence: newKeys[newKeys.length - 2],
      answers: getAnswerArray(quiz.answers, newKeys),
      properties: quiz.properties,
    };
  };

  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string,
    take: Take,
    actorPresets: Actor[],
  ): string[] => {
    const converted = interaction as IWordSpinnerQuiz;
    const warnings: string[] = [];

    ValidationHelper.ValidateKey(converted.title, getValue, warnings, "Title");

    return warnings;
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string,
    ) => SearchResultData[],
    getValue: (key: string) => string,
  ): SearchResultData[] => {
    const wordSpinnerQuiz = interaction as IWordSpinnerQuiz;

    let searchableStrings: string[] = [
      interaction.name,
      wordSpinnerQuiz.title,
      ...wordSpinnerQuiz.answers.map((item) => item.text),
    ];

    //Specific logic for wordspinner quiz
    let localFormattedSentence = getValue(wordSpinnerQuiz.formattedSentence);
    const pattern = /\{(\d+)\}/;
    const match = localFormattedSentence.match(pattern);

    if (match) {
      const index = parseInt(match[1], 10);
      if (index < wordSpinnerQuiz.answers.length) {
        // Replace only the first occurrence
        localFormattedSentence = localFormattedSentence.replace(
          match[0],
          getValue(wordSpinnerQuiz.answers[index].text),
        );
        searchableStrings.push(localFormattedSentence);
      }
    }

    return getMatchingResults(
      searchableStrings,
      takeName,
      interaction,
      filterType,
    );
  };

  const filterType: string = "Word Spinner";

  const validateErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[],
    allPropPresets: Prop[],
    localizedStringRecords: LocalizedStringRecord,
  ) => {
    const errors: string[] = [];
    const casted = interaction as IWordSpinnerQuiz;

    // logic goes here :)
    if (
      !localizedStringRecords ||
      !localizedStringRecords[casted.formattedSentence]
    )
      return errors;
    const langs = Object.keys(localizedStringRecords[casted.formattedSentence]);

    for (var i = 0; i < langs.length; i++) {
      const lang = langs[i];

      console.log("formattedSentence", casted.formattedSentence);
      console.log("lang", lang);
      const formattedSentence =
        localizedStringRecords[casted.formattedSentence][lang].value;
      const match = formattedSentence.match(/\{(\d+)\}/);
      if (!match) {
        errors.push(`${lang}: no correct answer selected`);
        continue;
      }

      const answerIndex = parseInt(match[1], 10);
      if (
        Number.isNaN(answerIndex) ||
        answerIndex < 0 ||
        answerIndex >= casted.answers.length
      ) {
        errors.push(`${lang}: no correct answer selected`);
      }
    }

    return errors;
  };

  return {
    View: component,
    FilterType: filterType,
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};
